import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CORRECTION_TYPES, useStudyOverview } from '../../../hooks/study/useStudyOverview';

const Report = () => {
  const { rows, loading } = useStudyOverview();
  const answered = rows.filter((row) => row.hasAnswer).length;
  const corrections = rows.reduce((sum, row) => sum + row.total, 0);
  const teacherCount = rows.filter((row) => row.teacherFeedback).length;
  const weaknessMap = new Map();

  rows.forEach((row) => {
    row.weaknesses.forEach((item) => {
      const current = weaknessMap.get(item.label) || { label: item.label, count: 0, sample: item.sample, lectureId: row.id, lectureName: row.name };
      current.count += item.count;
      weaknessMap.set(item.label, current);
    });
  });
  const weaknesses = [...weaknessMap.values()].sort((a, b) => b.count - a.count);

  return (
    <Page>
      <Kicker>리포트</Kicker>
      <Title>받은 첨삭을 한곳에 모았습니다</Title>
      <Lead>점수는 만들지 않습니다. 저장해 둔 첨삭 횟수와, 첨삭 문장에 반복된 표현만 보여 줍니다.</Lead>

      {loading ? (
        <Empty>첨삭 기록을 불러오는 중...</Empty>
      ) : (
        <>
          <Stats>
            <Stat>
              <strong>{answered}</strong>
              <span>답안을 쓴 문제</span>
            </Stat>
            <Stat>
              <strong>{corrections}</strong>
              <span>AI 첨삭 횟수</span>
            </Stat>
            <Stat>
              <strong>{teacherCount}</strong>
              <span>강사 첨삭이 온 문제</span>
            </Stat>
          </Stats>

          {rows.length === 0 ? (
            <Empty>등록된 문제가 없습니다.</Empty>
          ) : (
            rows.map((row) => (
              <Card key={row.id}>
                <CardTop>
                  <div>
                    <h2>{row.name}</h2>
                    <p>{row.hasAnswer ? '답안 저장됨' : '아직 저장된 답안 없음'}</p>
                  </div>
                  <Go to={`/lecture/${row.id}?step=3`}>AI 첨삭 보기</Go>
                </CardTop>
                <Bars>
                  {CORRECTION_TYPES.map((type) => (
                    <Bar key={type.key}>
                      <span>{type.label}</span>
                      <strong>{row.counts[type.key]}회</strong>
                    </Bar>
                  ))}
                </Bars>
                {row.teacherFeedback ? (
                  <Feedback>
                    <b>강사 첨삭</b>
                    <p>{row.teacherFeedback}</p>
                  </Feedback>
                ) : null}
              </Card>
            ))
          )}

          <SectionTitle>반복된 지적</SectionTitle>
          <Lead>첨삭 문장 안에 같은 단어가 여러 번 나오면 여기에 모읍니다.</Lead>
          {weaknesses.length === 0 ? (
            <Empty>아직 반복해서 잡힌 표현이 없습니다. AI 첨삭을 받으면 이곳에 쌓입니다.</Empty>
          ) : (
            <WeakList>
              {weaknesses.map((item) => (
                <Weak key={item.label}>
                  <Top>
                    <b>{item.label}</b>
                    <span>{item.count}번</span>
                  </Top>
                  {item.sample ? <p>{item.sample}</p> : null}
                  <Go to={`/lecture/${item.lectureId}?step=3`}>{item.lectureName}</Go>
                </Weak>
              ))}
            </WeakList>
          )}
        </>
      )}
    </Page>
  );
};

export default Report;

const Page = styled.div`
  max-width: 1040px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #d9412b;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #1d2340;
`;

const Lead = styled.p`
  margin: 8px 0 20px;
  font-size: 15px;
  line-height: 1.6;
  color: #6f6a5c;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  padding: 18px;
  background: #fff;
  border: 1px solid #e7e1d3;
  border-radius: 4px;

  strong {
    display: block;
    font-size: 28px;
    letter-spacing: -0.03em;
    color: #1d2340;
  }

  span {
    font-size: 13px;
    color: #6f6a5c;
  }
`;

const Card = styled.article`
  margin-bottom: 14px;
  padding: 20px;
  background: #fff;
  border: 1px solid #e7e1d3;
  border-radius: 4px;
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;

  h2 {
    margin: 0;
    font-size: 18px;
    color: #1d2340;
  }

  p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #6f6a5c;
  }
`;

const Go = styled(Link)`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: #1d2340;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: #d9412b;
  }
`;

const Bars = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 16px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Bar = styled.div`
  padding: 12px;
  background: #f8f5ee;
  border-radius: 4px;

  span {
    display: block;
    font-size: 12px;
    color: #6f6a5c;
  }

  strong {
    font-size: 18px;
    color: #1d2340;
  }
`;

const Feedback = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #e7e1d3;

  b {
    font-size: 13px;
    color: #d9412b;
  }

  p {
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 1.65;
    white-space: pre-wrap;
    color: #1d2340;
  }
`;

const SectionTitle = styled.h2`
  margin: 28px 0 0;
  font-size: 22px;
  color: #1d2340;
`;

const WeakList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Weak = styled.article`
  padding: 16px;
  background: #fffbea;
  border: 1px solid #efe4bd;
  border-radius: 4px;

  p {
    margin: 8px 0;
    font-size: 13px;
    line-height: 1.55;
    color: #1d2340;
  }
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  color: #1d2340;

  span {
    color: #d9412b;
    font-weight: 700;
  }
`;

const Empty = styled.p`
  margin: 0 0 16px;
  padding: 22px;
  background: #fff;
  border: 1px dashed #e7e1d3;
  border-radius: 4px;
  color: #6f6a5c;
`;
